import { useTranslation } from 'react-i18next'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
    CartesianGrid,
} from 'recharts'
import { CHART_COLORS, CHART_THEME } from '@/constants/chartColors'
import { QuarterlyMetrics } from '@/modules/Survey/types/quarterly-report'
import { useLegendToggle } from '../hooks/use-legend-toggle'

interface ReferralUpsaleChartProps {
    data: QuarterlyMetrics[]
}

export const ReferralUpsaleChart = ({ data }: ReferralUpsaleChartProps) => {
    const { t } = useTranslation('surveys')
    const upsaleToggle = useLegendToggle()

    const referralLabel = t('surveys.quarterly_report.referral_rating')

    // Avg referral score per quarter (1-10)
    const referralData = data.map((row) => ({
        quarter: row.quarter,
        [referralLabel]: row.avg_referral_willingness_rating,
    }))

    const cloudLabel = t('surveys.upsale.cloud')
    const cdnLabel = t('surveys.upsale.cdn')
    const vnisLabel = t('surveys.upsale.vnis')

    const upsaleData = data.map((row) => ({
        quarter: row.quarter,
        [cloudLabel]: row.upsale_potential.cloud,
        [cdnLabel]: row.upsale_potential.cdn,
        [vnisLabel]: row.upsale_potential.vnis,
    }))

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* Avg referral willingness score — bar chart (1-10) */}
            <div className="bg-white rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-4">
                    {t('surveys.quarterly_report.chart_referral_rating')}
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                        data={referralData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid {...CHART_THEME.grid} />
                        <XAxis
                            dataKey="quarter"
                            {...CHART_THEME.axis}
                            tickLine={false}
                        />
                        <YAxis {...CHART_THEME.axis} domain={[0, 10]} />
                        <Tooltip
                            contentStyle={CHART_THEME.tooltip.contentStyle}
                        />
                        <Bar
                            dataKey={referralLabel}
                            fill={CHART_COLORS.EMERALD_GREEN}
                            radius={[3, 3, 0, 0]}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Upsale — grouped bars */}
            <div className="bg-white rounded-lg p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-700 mb-4">
                    {t('surveys.quarterly_report.chart_upsale')}
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                        data={upsaleData}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                        <CartesianGrid {...CHART_THEME.grid} />
                        <XAxis
                            dataKey="quarter"
                            {...CHART_THEME.axis}
                            tickLine={false}
                        />
                        <YAxis {...CHART_THEME.axis} />
                        <Tooltip
                            contentStyle={CHART_THEME.tooltip.contentStyle}
                        />
                        <Legend
                            wrapperStyle={{
                                fontSize: '12px',
                                paddingTop: '8px',
                                cursor: 'pointer',
                            }}
                            iconSize={10}
                            onClick={upsaleToggle.handleLegendClick}
                        />
                        <Bar
                            dataKey={cloudLabel}
                            fill={CHART_COLORS.PRIMARY_BLUE}
                            radius={[3, 3, 0, 0]}
                            hide={upsaleToggle.isHidden(cloudLabel)}
                        />
                        <Bar
                            dataKey={cdnLabel}
                            fill={CHART_COLORS.AMBER_ORANGE}
                            radius={[3, 3, 0, 0]}
                            hide={upsaleToggle.isHidden(cdnLabel)}
                        />
                        <Bar
                            dataKey={vnisLabel}
                            fill={CHART_COLORS.INDIGO_PURPLE}
                            radius={[3, 3, 0, 0]}
                            hide={upsaleToggle.isHidden(vnisLabel)}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}
